import {
  Autocomplete,
  FormControl,
  FormHelperText,
  TextField,
} from "@mui/material";
import { useCallback, useState, type FC } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ValidationHelpers, type IInput } from "@ryde/shared";

import {
  DropdownOptionRenderer,
  useTechnicalNameOptions,
} from "../../../dynamic-forms";

// name will be relative to the control of form passed to the input
// can be used to create independent nested forms
export const DropdownInputByControl: FC<IInput> = ({
  control,
  name,
  formLabel,
  formAttributes,
  formValidation,
  className,
}) => {
  const dropDownItems = formAttributes?.selectItems;

  const initial = null;

  const values = dropDownItems?.map(item => {
    return !isNaN(parseInt(`${item.value}`, 10)) ? +item.value : item.value;
  }) as (string | number | boolean)[];

  const [innerValue, setInnerValue] = useState<
    string | number | boolean | null
  >(initial);
  const [inputValue, setInputValue] = useState<string>(
    dropDownItems?.find(item => item.value === initial)?.label || "",
  );
  const { t } = useTranslation();
  const field = t("errors.requiredField", {
    fieldName: formLabel || t("general.THIS_FIELD"),
  });

  const getOptionLabel = useCallback(
    (value: string | number | boolean) =>
      dropDownItems?.find(item => item.value === value)?.label || "",
    [dropDownItems],
  );
  const { technicalNames, filterOptions } = useTechnicalNameOptions({
    selectItems: dropDownItems,
    getOptionLabel,
  });

  return name ? (
    <Controller
      control={control}
      name={name}
      defaultValue={initial}
      rules={{
        required: ValidationHelpers.hasRequiredRule(formValidation) && field,
      }}
      render={({
        field: { value: formValue, onChange, ...restField },
        fieldState: { error },
      }) => (
        <FormControl
          required={ValidationHelpers.hasRequiredRule(formValidation)}
          fullWidth
          error={!!error}
          className={className}
        >
          <Autocomplete
            {...restField}
            data-e2e-id={`dropdown_${name}`}
            defaultValue={initial}
            options={values || []}
            value={innerValue || formValue}
            inputValue={inputValue}
            fullWidth
            onChange={(_, data) => {
              setInnerValue(data);
              onChange(data);
            }}
            onInputChange={(_, data) => {
              setInputValue(data);
            }}
            getOptionLabel={getOptionLabel}
            filterOptions={filterOptions}
            renderOption={
              filterOptions &&
              ((props, option) =>
                DropdownOptionRenderer({
                  listItemProps: props,
                  option: option as string | number,
                  getOptionLabel,
                  technicalNames,
                }))
            }
            renderInput={params => (
              <TextField
                {...params}
                error={!!error}
                label={formLabel}
                required={ValidationHelpers.hasRequiredRule(formValidation)}
                fullWidth
              />
            )}
          />
          {error && <FormHelperText>{error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  ) : null;
};
